/**
 * Thanksgiving phone wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingPhoneWallpapersPack: ToilePack = {
    slug: 'thanksgiving-phone-wallpapers',
    category: 'season',
    name: 'Thanksgiving phone wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'gourds', label: 'Pumpkin pile', subject: 'A pile of pumpkins and gourds at the bottom of the frame with a few leaves, a calm warm ground above'},
        {key: 'turkey', label: 'Turkey in leaves', subject: 'A turkey with a fanned tail standing among fallen leaves at the bottom, a calm sky above'},
        {key: 'wheat', label: 'Wheat sheaf', subject: 'A sheaf of wheat tied with a ribbon at the lower edge, a calm ground above'},
    ],
    styles: [
        {key: 'painted', label: 'Painted', style: 'soft painted illustration of autumn, warm light', palette: 'Rust, amber and olive', framing: 'The focal point in the lower third and the upper half calm for the clock and icons'},
        {key: 'minimal', label: 'Minimal', style: 'minimal flat illustration, few shapes, lots of calm ground', palette: 'Rust and mustard on cream', framing: 'A quiet center for icons, the motif at the edges'},
        {key: 'cozy', label: 'Cozy', style: 'cozy flat illustration, rounded shapes, warm and quiet', palette: 'Pumpkin orange, brown and cream', framing: 'The focal point low, the ground calm above for widgets'},
    ],
    directions: ['a cornucopia instead', 'in a cooler color palette', 'for a tablet screen'],
    directionHint: 'try: a cornucopia instead',
    seo: {
        title: 'Thanksgiving phone wallpapers: gourds, turkey',
        metaDescription: 'Nine Thanksgiving lock screen wallpapers — a pumpkin pile, a turkey in leaves, a wheat sheaf — in painted, minimal or cozy style, calm top for icons.',
        h1: 'Thanksgiving phone wallpaper: pumpkin pile, turkey, wheat sheaf',
        tagline: 'Nine wallpapers, three November scenes, three styles each — the top stays calm so the clock and icons sit clean.',
        intro: "A lock screen has to leave room for the clock — the top can't be busy. These nine set a pile of pumpkins and gourds, a turkey standing among fallen leaves, and a wheat sheaf tied with ribbon, each as painted, minimal or cozy. Ask for a different scene, like a cornucopia, and the same calm top carries it.",
        useCases: [
            'an iPhone or Android lock screen',
            'a cozy fall phone background',
            'a calm background behind widgets and the clock',
            'a matching wallpaper across phone and tablet',
        ],
        faq: [
            {q: 'Will the clock and icons stay readable over the wallpaper?', a: 'Yes — every wallpaper keeps its top calm and low-contrast, so the clock, notifications and app icons stay legible over it.'},
            {q: 'What size do these render at?', a: 'Portrait phone proportions, composed so the focal point sits low and the top third stays quiet.'},
            {q: 'Can I request a different scene, like a cornucopia?', a: 'Yes — the direction field takes any November scene, and the same three styles render it with a calm top.'},
            {q: "What's the difference between painted, minimal and cozy?", a: 'Painted is a soft warm-lit autumn illustration, minimal is a flat few-shape illustration, and cozy is a rounded warm flat illustration.'},
        ],
    },
}
